import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../config/axios';
import { useSocket } from '../contexts/SocketContext';
import {
  ArrowLeft,
  ExternalLink,
  Clock,
  TrendingUp,
  Activity,
  Calendar,
  AlertTriangle,
  Wifi
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import StatusBadge from '../components/UI/StatusBadge';

const MonitorDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { socket, connected } = useSocket();
  const [monitor, setMonitor] = useState(null);
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('24h');

  const periods = [
    { value: '1h', label: '1 Hour' },
    { value: '24h', label: '24 Hours' },
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' }
  ];

  useEffect(() => {
    fetchMonitorDetails();
  }, [id]);

  useEffect(() => {
    if (monitor) {
      fetchMonitorStats();
    }
  }, [monitor, selectedPeriod]);

  // Listen for real-time updates for this specific monitor
  useEffect(() => {
    if (!socket || !monitor) return;

    const handleMonitorUpdate = (data) => {
      // Only update if it's for this monitor
      if (data.monitorId === id) {
        console.log('📡 Real-time update for monitor:', data);

        // Update monitor status
        setMonitor(prev => ({
          ...prev,
          status: data.status,
          uptime: data.uptime,
          lastChecked: data.timestamp
        }));

        // Show notification for status changes
        if (data.status === 'down') {
          toast.error(`${monitor.name} is down!`, {
            icon: '🔴',
            duration: 4000,
          });
        } else if (data.status === 'up') {
          toast.success(`${monitor.name} is back online!`, {
            icon: '🟢',
            duration: 3000,
          });
        }
      }
    };

    socket.on('monitorUpdate', handleMonitorUpdate);

    return () => {
      socket.off('monitorUpdate', handleMonitorUpdate);
    };
  }, [socket, monitor, id]);

  const fetchMonitorDetails = async () => {
    try {
      const response = await api.get(`/api/monitors/${id}`);
      setMonitor(response.data.monitor);
      setRecentLogs(response.data.recentLogs);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Monitor not found');
        navigate('/dashboard');
      } else {
        toast.error('Failed to fetch monitor details');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMonitorStats = async () => {
    try {
      const response = await api.get(`/api/monitors/${id}/stats?period=${selectedPeriod}`);
      setStats(response.data.stats);
      setChartData(response.data.chartData);
    } catch (error) {
      console.error('Failed to fetch monitor stats:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-50 dark:bg-surface-950 flex flex-col items-center justify-center space-y-4">
        <LoadingSpinner size="lg" />
        <p className="text-[10px] font-bold text-surface-400 uppercase tracking-widest animate-pulse">Initializing Monitor Analytics...</p>
      </div>
    );
  }

  if (!monitor) {
    return (
      <div className="min-h-screen bg-surface-50 dark:bg-surface-950 flex items-center justify-center">
        <div className="card-premium p-12 text-center max-w-md mx-auto space-y-6">
          <div className="p-4 bg-danger/10 rounded-3xl inline-block">
             <AlertTriangle className="h-10 w-10 text-danger mx-auto" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-surface-900 dark:text-white mb-2">
              Monitor Not Found
            </h2>
            <p className="text-sm text-surface-500 font-medium leading-relaxed">
               The monitor ID provided does not exist or has been decommissioned. Please verify the URL or return to your active dashboard.
            </p>
          </div>
          <Link to="/dashboard" className="btn-primary !px-8 !py-3 inline-block font-bold">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-950 transition-colors duration-200 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header Section */}
        <div className="mb-12">
          <Link
            to="/dashboard"
            className="group inline-flex items-center text-sm font-bold text-surface-500 hover:text-brand-600 dark:hover:text-brand-400 mb-6 transition-colors uppercase tracking-widest"
          >
            <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Return to Dashboard
          </Link>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <StatusBadge status={monitor.status} />
                <div className="h-1 w-1 rounded-full bg-surface-300 dark:bg-surface-700"></div>
                <span className="text-xs font-bold text-surface-500 uppercase tracking-[0.2em]">Node ID: {id.slice(-6).toUpperCase()}</span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-surface-900 dark:text-white tracking-tight">
                {monitor.name}
              </h1>

              <div className="flex flex-wrap items-center gap-4">
                <a
                  href={monitor.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 rounded-xl bg-surface-100 dark:bg-surface-800/50 text-surface-600 dark:text-surface-300 hover:text-brand-600 dark:hover:text-brand-400 border border-surface-200 dark:border-surface-700/50 transition-all group"
                >
                  <span className="font-semibold">{monitor.url}</span>
                  <ExternalLink className="h-3 w-3 ml-2 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>

                <div className="flex items-center px-4 py-2 rounded-xl bg-surface-100 dark:bg-surface-800/50 border border-surface-200 dark:border-surface-700/50">
                  {connected ? (
                    <div className="flex items-center space-x-2">
                      <div className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
                      </div>
                      <span className="text-xs font-bold text-surface-600 dark:text-surface-400 uppercase tracking-widest">Live Updates</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Wifi className="h-4 w-4 text-surface-400" />
                      <span className="text-xs font-bold text-surface-400 uppercase tracking-widest">Connecting...</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-row gap-4">
              {/* Any header-level actions could go here */}
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {/* Uptime Card */}
            <div className="card-premium p-6 group">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-brand-500/10 rounded-2xl group-hover:bg-brand-500/20 transition-colors">
                  <TrendingUp className="h-6 w-6 text-brand-600 dark:text-brand-400" />
                </div>
                <span className="text-xs font-bold text-surface-400">Past {selectedPeriod}</span>
              </div>
              <div className="space-y-1">
                <p className="text-3xl font-bold text-gradient">{stats.uptime}%</p>
                <p className="text-sm font-semibold text-surface-500 uppercase tracking-wider">Historical Uptime</p>
              </div>
            </div>

            {/* Success Card */}
            <div className="card-premium p-6 group border-l-4 border-l-success/30">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-success/10 rounded-2xl group-hover:bg-success/20 transition-colors">
                  <Activity className="h-6 w-6 text-success" />
                </div>
                <div className="flex items-center space-x-1 px-2 py-1 rounded-full bg-success/10">
                  <div className="h-1 w-1 rounded-full bg-success"></div>
                  <span className="text-[10px] font-bold text-success uppercase">Pinging</span>
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-3xl font-bold text-surface-900 dark:text-white">{stats.successfulPings}</p>
                <p className="text-sm font-semibold text-surface-500 uppercase tracking-wider">Samples Recorded</p>
              </div>
            </div>

            {/* Avg Response Card */}
            <div className="card-premium p-6 group">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-info/10 rounded-2xl group-hover:bg-info/20 transition-colors">
                  <Clock className="h-6 w-6 text-info" />
                </div>
                <span className="text-xs font-bold text-surface-400">Total Avg</span>
              </div>
              <div className="space-y-1">
                <p className="text-3xl font-bold text-surface-900 dark:text-white">{stats.avgResponseTime}ms</p>
                <p className="text-sm font-semibold text-surface-500 uppercase tracking-wider">Median Latency</p>
              </div>
            </div>

            {/* Total Checks Card */}
            <div className="card-premium p-6 group">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-surface-100 dark:bg-surface-800 rounded-2xl">
                  <Calendar className="h-6 w-6 text-surface-600 dark:text-surface-400" />
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-3xl font-bold text-surface-900 dark:text-white">{stats.totalPings}</p>
                <p className="text-sm font-semibold text-surface-500 uppercase tracking-wider">Total Checks</p>
              </div>
            </div>
          </div>
        )}

        {/* Dynamic Analytics Section */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Chart Container */}
          <div className="xl:col-span-2 space-y-8">
            <div className="card-premium p-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-10">
                <div>
                  <h2 className="text-xl font-bold text-surface-900 dark:text-white mb-1">Performance Analytics</h2>
                  <p className="text-sm text-surface-500 font-medium tracking-tight">Visualizing real-time and historical uptime data</p>
                </div>

                <div className="flex p-1 bg-surface-100 dark:bg-surface-800/80 rounded-2xl border border-surface-200 dark:border-surface-700/50">
                  {periods.map((period) => (
                    <button
                      key={period.value}
                      onClick={() => setSelectedPeriod(period.value)}
                      className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${selectedPeriod === period.value
                        ? 'bg-white dark:bg-surface-700 text-brand-600 dark:text-white shadow-premium-sm'
                        : 'text-surface-500 hover:text-surface-900 dark:hover:text-surface-300'
                        }`}
                    >
                      {period.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="h-[22rem] relative">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorUptime" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.1} />
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#e2e8f01a" />
                      <XAxis
                        dataKey="timestamp"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 10, fontWeight: 600 }}
                        tickFormatter={(value) => new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        minTickGap={30}
                      />
                      <YAxis
                        domain={[0, 100]}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 10, fontWeight: 600 }}
                        tickFormatter={(value) => `${value}%`}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#0f172a',
                          borderColor: '#1e293b',
                          borderRadius: '16px',
                          boxShadow: '0 10px 15px -3px rgba(0,0,0,0.5)',
                          border: '1px solid rgba(255,255,255,0.1)'
                        }}
                        itemStyle={{ color: '#8b5cf6', fontWeight: 700 }}
                        labelStyle={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}
                        labelFormatter={(value) => new Date(value).toLocaleString()}
                        formatter={(value) => [`${value.toFixed(2)}%`, 'Service Uptime']}
                      />
                      <Line
                        type="monotone"
                        dataKey="uptime"
                        stroke="#8b5cf6"
                        strokeWidth={4}
                        dot={false}
                        activeDot={{ r: 6, strokeWidth: 0, fill: '#a78bfa' }}
                        animationDuration={1500}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-surface-500">
                    <Activity className="h-12 w-12 mb-4 opacity-10 animate-float" />
                    <p className="text-sm font-bold uppercase tracking-widest">Aggregation in progress...</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Activity Log Container */}
          <div className="xl:col-span-1">
            <div className="card-premium h-full flex flex-col">
              <div className="p-8 border-b border-surface-100 dark:border-surface-800/50">
                <h2 className="text-xl font-bold text-surface-900 dark:text-white mb-1">Recent Activity</h2>
                <p className="text-sm text-surface-500 font-medium tracking-tight">Latest status verification attempts</p>
              </div>

              <div className="flex-1 overflow-y-auto px-8 py-4 custom-scrollbar max-h-[30rem] lg:max-h-none">
                {recentLogs.length > 0 ? (
                  <div className="space-y-6">
                    {recentLogs.slice(0, 15).map((log, index) => (
                      <div key={index} className="flex items-center justify-between group">
                        <div className="flex items-center space-x-4">
                          <div className={`relative flex h-3 w-3`}>
                            {log.status === 'success' ? (
                              <span className="relative inline-flex rounded-full h-3 w-3 bg-success/40 group-hover:bg-success transition-colors"></span>
                            ) : (
                              <span className="relative inline-flex rounded-full h-3 w-3 bg-danger animate-pulse"></span>
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-surface-900 dark:text-white group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                              {log.status === 'success' ? 'Health Check Passed' : 'Health Check Failed'}
                            </p>
                            <p className="text-[10px] font-semibold text-surface-500 uppercase tracking-wider">
                              {formatDate(log.timestamp)}
                            </p>
                          </div>
                        </div>

                        <div className="text-right">
                          {log.status === 'success' ? (
                            <div className="flex flex-col">
                              <span className="text-sm font-bold text-brand-600 dark:text-brand-400">
                                {log.responseTime}ms
                              </span>
                              <span className="text-[10px] font-bold text-surface-400">HTTP {log.statusCode}</span>
                            </div>
                          ) : (
                            <div className="flex flex-col">
                              <span className="text-sm font-bold text-danger animate-pulse">
                                ERROR
                              </span>
                              <span className="text-[10px] font-bold text-danger opacity-60">Timeout</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-20 text-center">
                    <Activity className="h-10 w-10 text-surface-200 dark:text-surface-800 mx-auto mb-4" />
                    <p className="text-xs font-bold text-surface-400 uppercase tracking-widest">Waiting for data logs...</p>
                  </div>
                )}
              </div>

              <div className="p-6 bg-surface-50 dark:bg-surface-800/20 border-t border-surface-100 dark:border-surface-800/50 rounded-b-[2rem]">
                <button
                  onClick={() => fetchMonitorDetails()}
                  className="w-full text-xs font-bold text-surface-500 hover:text-brand-600 dark:hover:text-brand-400 transition-colors uppercase tracking-[0.2em]"
                >
                  Refresh Activity Feed
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonitorDetails;
